import { Plus, Search, UserCog } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { User as UserData } from '@/api/generated/model/users';
import { useListUsers } from '@/api/generated/users';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UserDialog } from './UserDialog';

export default function UserList() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const search = params.get('search') || undefined;
  const role = params.get('role') || undefined;

  const {
    data: result,
    isLoading: loading,
    refetch: fetchUsers,
  } = useListUsers({
    search,
    role,
  });

  const users = result?.data || [];
  // biome-ignore lint/suspicious/noExplicitAny: API type generated by Orval doesn't include total in this version
  const total = (result as any)?.total || users.length;

  // Dialog State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

  const handleSearch = (term: string) => {
    const newParams = new URLSearchParams(params);
    if (term) newParams.set('search', term);
    else newParams.delete('search');
    setParams(newParams);
  };

  const handleRoleFilter = (role: string) => {
    const newParams = new URLSearchParams(params);
    if (role && role !== 'all') newParams.set('role', role);
    else newParams.delete('role');
    setParams(newParams);
  };

  const openCreate = () => {
    setSelectedUser(null);
    setIsDialogOpen(true);
  };

  const openEdit = (user: UserData) => {
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
            <UserCog className="w-6 h-6 text-blue-600" />
            {t('userList.title', 'User Management')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('userList.description', 'Manage administrator accounts and roles.')}
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="w-4 h-4" />
          {t('common.create', 'Create User')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-lg border shadow-sm">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('common.search', 'Search by name or email...')}
            className="pl-9"
            defaultValue={params.get('search') || ''}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <Select defaultValue={params.get('role') || 'all'} onValueChange={handleRoleFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="system_admin">System Admin</SelectItem>
            <SelectItem value="univ_admin">University Admin</SelectItem>
            <SelectItem value="dept_staff">Dept Staff</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-md border bg-white shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            )}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No users found.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{u.name}</span>
                      <span className="text-xs text-gray-500">{u.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={u.status} />
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{u.departmentCode || '-'}</TableCell>
                  <TableCell className="text-sm text-gray-600">{new Date(u.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination (Simplified) */}
      <div className="text-xs text-gray-500 text-right">Total: {total} users</div>

      <UserDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} user={selectedUser} onSuccess={fetchUsers} />
    </div>
  );
}

function StatusBadge({ status }: Readonly<{ status: string }>) {
  if (status === 'active') return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>;
  if (status === 'suspended') return <Badge variant="destructive">Suspended</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}
